import React, { useState, useEffect, useMemo } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../UI/tooltip';
import SahiSpeech from '../../Utils/SahiSpeech';

export function SpeechControls({
    targetId,
    value,
    onChange,
    isMobile = false,
    className = "absolute right-2 top-2",
    onSpeechComplete = null
}) {
    const [recording, setRecording] = useState(false);
    const [voiceLangCode, setVoiceLangCode] = useState(() => localStorage.getItem('taskVoiceLangCode') || 'en-IN');

    // Mapped languages supported by SahiSpeech
    const voiceLanguages = useMemo(() => [
        { code: 'en-IN', label: 'ENG' },
        { code: 'hi-IN', label: 'HIN' },
        { code: 'mr-IN', label: 'MAR' },
        { code: 'gu-IN', label: 'GUJ' }
    ], []);

    const cycleVoiceLanguage = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (recording) {
            SahiSpeech.stop();
            setRecording(false);
        }
        setVoiceLangCode(prev => {
            const idx = voiceLanguages.findIndex(l => l.code === prev);
            const next = voiceLanguages[(idx + 1) % voiceLanguages.length].code;
            localStorage.setItem('taskVoiceLangCode', next);
            return next;
        });
    };

    const toggleSpeechRecognition = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (recording) {
            SahiSpeech.stop();
            setRecording(false);
        } else {
            setRecording(true);
            SahiSpeech.start(
                targetId,
                (text) => {
                    const updatedVal = (value ? value + " " + text : text).trim();
                    onChange && onChange(updatedVal);
                    setRecording(false);
                    if (onSpeechComplete) {
                        onSpeechComplete(updatedVal);
                    }
                },
                (err) => {
                    console.error("Speech recognition error:", err);
                    setRecording(false);
                },
                voiceLangCode
            );
        }
    };

    // Ensure speech stops on unmount
    useEffect(() => {
        return () => {
            SahiSpeech.stop();
        };
    }, []);

    return (
        <div className={`${className} flex items-center gap-1.5 z-10`}>
            {isMobile ? (
                <button
                    type="button"
                    onClick={cycleVoiceLanguage}
                    className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full border border-gray-200 hover:border-sahi-primary-color hover:text-sahi-primary-color text-sub-text-color focus:outline-none bg-white cursor-pointer select-none"
                >
                    {voiceLanguages.find(l => l.code === voiceLangCode)?.label || 'ENG'}
                </button>
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={cycleVoiceLanguage}
                                className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full border border-gray-200 hover:border-sahi-primary-color hover:text-sahi-primary-color text-sub-text-color focus:outline-none bg-white cursor-pointer select-none"
                            >
                                {voiceLanguages.find(l => l.code === voiceLangCode)?.label || 'ENG'}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Voice input language</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}

            {isMobile ? (
                <button
                    type="button"
                    onClick={toggleSpeechRecognition}
                    className={`p-1.5 rounded-full text-gray-500 hover:text-sahi-primary-color transition-all duration-200 focus:outline-none cursor-pointer bg-white ${
                        recording ? 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 animate-pulse' : ''
                    }`}
                >
                    {recording ? (
                        <MicOff className="h-4 w-4" />
                    ) : (
                        <Mic className="h-4 w-4" />
                    )}
                </button>
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={toggleSpeechRecognition}
                                className={`p-1.5 rounded-full text-gray-500 hover:text-sahi-primary-color transition-all duration-200 focus:outline-none cursor-pointer bg-white ${
                                    recording ? 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 animate-pulse' : ''
                                }`}
                            >
                                {recording ? (
                                    <MicOff className="h-4 w-4" />
                                ) : (
                                    <Mic className="h-4 w-4" />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{recording ? 'Stop voice input' : 'Start voice input'}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}
